import { DarkMode, useDisclosure } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { FloatingBottomMultiselect } from 'ui-components';
import { SavedListCreateListModal } from '~/components/SavedListCreateListModal';
import { SavedListShareModal } from '~/components/SavedListShareModal';
import { useCreateList } from '~/hooks/api/useSavedLists';
import { useSavedListsStore } from '~/screens/MyAgency/store';
import { ICreator } from '~/types';

import { AddingToAnExistingListModal } from '../../SavedListButton/AddingToAnExistingListModal';
import { useHasSavedList } from './FloatingMultiSelect.utils';

interface Props {
  creators: ICreator[];
  hasFinishedLoading?: boolean;
}

export const FloatingMultiselectTalent: React.FC<Props> = ({
  creators,
  hasFinishedLoading = false,
}) => {
  const hasSavedLists = useHasSavedList();
  const { mutateAsync } = useCreateList();

  const {
    isOpen: isOpenCreateSavedList,
    onClose: onCloseCreateSavedList,
    onOpen: onOpenCreateSavedList,
  } = useDisclosure();

  const {
    isOpen: isOpenAddSavedList,
    onClose: onCloseAddSavedList,
    onOpen: onAddSavedList,
  } = useDisclosure();

  const {
    isOpen: isOpenShareModal,
    onClose: onCloseShareModal,
    onOpen: onOpenShareModal,
  } = useDisclosure();

  const {
    creators: selectedCreators,
    clearStore,
    toggleAllCreators,
  } = useSavedListsStore();

  const isAllSelected = useMemo(
    () =>
      creators
        ? selectedCreators.length === (creators as ICreator[]).length
        : false,
    [selectedCreators, creators],
  );

  const handleSelectAllCreators = useCallback(() => {
    toggleAllCreators(creators as ICreator[], !isAllSelected);
  }, [toggleAllCreators, creators, isAllSelected]);

  const SavedList = useCallback(() => {
    if (hasSavedLists) {
      onAddSavedList();
    } else {
      onOpenCreateSavedList();
    }
  }, [hasSavedLists, onAddSavedList, onOpenCreateSavedList]);

  const handleGenerateShareLink = useCallback(
    async (enabledControls: string[]) => {
      const { shareLink } = await mutateAsync({
        creatorIds: selectedCreators.map(({ id }) => id),
        enabledControls: enabledControls,
        saved: false,
      });
      return shareLink;
    },
    [selectedCreators, mutateAsync],
  );

  return (
    <>
      <SavedListShareModal
        isOpen={isOpenShareModal}
        onClose={onCloseShareModal}
        onGenerateLink={handleGenerateShareLink}
      />
      {hasSavedLists ? (
        <AddingToAnExistingListModal
          isOpen={isOpenAddSavedList}
          onClose={onCloseAddSavedList}
        />
      ) : (
        <SavedListCreateListModal
          creatorIds={creators.map(({ id }) => id)}
          isOpen={isOpenCreateSavedList}
          onClose={onCloseCreateSavedList}
          onSuccess={clearStore}
        />
      )}
      <DarkMode>
        <FloatingBottomMultiselect
          closeButtonProps={{
            trackId: 'foam-bulk-action-clear',
          }}
          isOpen={selectedCreators.length > 0}
          numberOfItems={selectedCreators.length}
          onClose={() => clearStore()}
          selectAll={{
            onClick: handleSelectAllCreators,
            children: 'Select all',
            isDisabled: isAllSelected || !hasFinishedLoading,
            isLoading: !hasFinishedLoading,
            trackId: 'foam-bulk-action-select-all',
          }}
          primary={{
            onClick: SavedList,
            trackId: 'foam-bulk-action-add-to-list',
          }}
          secondary={{
            children: 'Share',
            onClick: onOpenShareModal,
            trackId: 'foam-bulk-action-share',
          }}
        />
      </DarkMode>
    </>
  );
};
